import React from "react";
import {
  Flex,
  Button,
  IconButton,
  Text,
  Select,
  Stack,
  useColorModeValue,
} from "@chakra-ui/react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { useTranslation } from "react-i18next";

interface PaginationProps {
  pageNo: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  pageNo,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const { t } = useTranslation();
  const totalPages = Math.ceil(totalCount / pageSize);
  const textColor = useColorModeValue("gray.700", "white");
  // Function to generate visible pages with ellipses
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const delta = 2;

    const left = Math.max(2, pageNo - delta);
    const right = Math.min(totalPages - 1, pageNo + delta);

    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");
    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  const pages = getPageNumbers();

  return (
    <Flex
      justify="space-between"
      align="center"
      wrap="wrap"
      gap={4}
      py={4}
      borderTop="1px solid"
      borderColor={useColorModeValue("gray.200", "gray.700")}
    >
      {/* Entries Selector */}
      <Stack direction="row" align="center">
        <Text fontSize="sm" color={textColor}>
          {t("common.list.show")}
        </Text>
        <Select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          w={20}
          size="sm"
        >
          {[5, 10, 20, 30, 40, 50].map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </Select>
        <Text fontSize="sm" color={textColor}>
          {t("common.list.entries")}
        </Text>
      </Stack>

      {/* Pagination Numbers */}
      <Stack direction="row" align="center" spacing={1}>
        <IconButton
          aria-label="Previous"
          icon={<MdChevronLeft />}
          size="sm"
          variant="ghost"
          isDisabled={pageNo <= 1}
          onClick={() => onPageChange(pageNo - 1)}
        />

        {pages.map((p, i) =>
          typeof p === "number" ? (
            <Button
              key={i}
              size="sm"
              bg={p === pageNo ? "brand.500" : "transparent"}
              color={p === pageNo ? "white" : textColor}
              onClick={() => onPageChange(p)}
            >
              {p}
            </Button>
          ) : (
            <Text key={i} px={2}>
              {p}
            </Text>
          )
        )}

        <IconButton
          aria-label="Next"
          icon={<MdChevronRight />}
          size="sm"
          variant="ghost"
          isDisabled={pageNo >= totalPages}
          onClick={() => onPageChange(pageNo + 1)}
        />
      </Stack>

      {/* Page Info */}

      <Text>
        {t("common.list.pageCountView", {
          pageNo,
          count:
            pageNo > 1
              ? pageNo * pageSize > totalCount
                ? totalCount
                : pageNo * pageSize
              : pageSize,
          maxPage: totalCount,
        })}
      </Text>
    </Flex>
  );
};

export default Pagination;
